import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'

import BorrowerSearchForm from './lib/BorrowerSearchForm'
import {
  BorrowerSearchInputColonValidator,
  BorrowerSearchInputOptimizer,
  BorrowerSearchInputSubmitHandler,
  SearchPart,
} from './lib/BorrowerSearchInput'
import { BorrowerSearchInputChangeHandler } from './lib/BorrowerSearchInputClearButton'
import { BorrowerSearchInputSanitizer } from './lib/BorrowerSearchToolbar'
import queryString, { isKeyValid } from './lib/queryString'

type BorrowerSearchProps = {
  setSearchQuery: Dispatch<SetStateAction<string>>
}

const peachPublicIdRegExp = [
  {
    key: 'borrowerId',
    regExp: /BO-[\w\d]{4}-[\w\d]{4}/,
  },
  {
    key: 'caseId',
    regExp: /CS-[\w\d]{4}-[\w\d]{4}/,
  },
  {
    key: 'interactionId',
    regExp: /IN-[\w\d]{4}-[\w\d]{4}/,
  },
  {
    key: 'accountId',
    regExp: /LN-[\w\d]{4}-[\w\d]{4}/,
  },
]

const BorrowerSearch: FC<BorrowerSearchProps> = ({ setSearchQuery }) => {
  const [searchInputValue, setSearchInputValue] = useState<string>('')
  const [submittedSearchInputValue, setSubmittedSearchInputValue] =
    useState<string>('')

  useEffect(() => {
    const parsedSearch = queryString.toString(
      submittedSearchInputValue,
    ) as unknown as string
    setSearchQuery(parsedSearch)
  }, [submittedSearchInputValue, setSearchQuery])

  const searchParts: Array<SearchPart> = useMemo(() => {
    const resolvedParts = [{ key: '', value: '', valid: true }]

    let isParamPair = false
    let isMultiWordValue = false

    for (const char of searchInputValue) {
      const currPart = resolvedParts[resolvedParts.length - 1]

      if (char === ':') {
        // parameter value entry is starting, flip switch and handle
        isParamPair = true

        // get the last word before the colon; this is the key
        const wordsBeforeColon = currPart.value.split(' ')
        const key = wordsBeforeColon[wordsBeforeColon.length - 1]
        const currValueTrimmed = currPart.value.trim()

        if (currPart.key !== '' || currPart.value === '') {
          // the user is breaking the law (pasting colons in)
          currPart.value += char
          continue
        }

        if (currPart.value === key || currValueTrimmed === key) {
          // the key is the only word in the current search part
          const numSpacesNeeded =
            currPart.value.length - currValueTrimmed.length
          // update current searchPart to be key/value pair
          currPart.key = key
          currPart.value = ''
          currPart.valid = isKeyValid(currValueTrimmed)
          // account for extra whitespace before the key if needed
          if (numSpacesNeeded > 0) {
            resolvedParts.splice(resolvedParts.length - 1, 0, {
              key: '',
              value: ' '.repeat(numSpacesNeeded),
              valid: true,
            })
          }
          continue
        } else {
          // there are multiple words preceeding this colon
          // Remove the last occurrence of the key (not the first)
          const lastKeyIndex = currPart.value.lastIndexOf(key)
          const valueWithoutKey =
            currPart.value.substring(0, lastKeyIndex) +
            currPart.value.substring(lastKeyIndex + key.length)
          const valueWithoutKeyTrimmed = valueWithoutKey.trim()
          const numSpacesNeeded =
            valueWithoutKey.length - valueWithoutKeyTrimmed.length
          // remove new key from current searchPart value
          currPart.value = valueWithoutKeyTrimmed
          // account for extra whitespace before the key if needed
          if (numSpacesNeeded > 0) {
            resolvedParts.push({
              key: '',
              value: ' '.repeat(numSpacesNeeded),
              valid: true,
            })
          }
          // create a new searchPart with the new key
          resolvedParts.push({ key, value: '', valid: isKeyValid(key) })
          continue
        }
      }

      if (char === '"' && isParamPair) {
        // multi-word value entry is starting or ending
        if (isMultiWordValue) {
          // multi-word value entry is ending
          isParamPair = false
          isMultiWordValue = false
          currPart.value += char
          resolvedParts.push({ key: '', value: '', valid: true })
          continue
        } else {
          // multi-word value entry is starting
          isMultiWordValue = true
          currPart.value += char
          continue
        }
      }

      if (char === ' ' && isParamPair && !isMultiWordValue) {
        // the user is entering a space and is not within quotes)
        // create a new searchPart to capture this text
        isParamPair = false
        resolvedParts.push({ key: '', value: char, valid: true })
        continue
      }

      if (!isParamPair && currPart.key !== '') {
        // freeform text is being entered and we don't have anywhere to put it yet
        resolvedParts.push({ key: '', value: char, valid: true })
        continue
      }

      // no special characters need handling, concat the character and validate if necessary
      currPart.value += char
      if (currPart.key !== '') currPart.valid = isKeyValid(currPart.key)
    }

    return resolvedParts
  }, [searchInputValue])

  const { isOptimized, optimizedSearchParts } = useMemo(() => {
    let isOptimized = true
    const optimizedSearchParts: Array<SearchPart> = [...searchParts]

    // loop through searchParts to find optimization opportunities
    for (let i = 0; i < optimizedSearchParts.length; i++) {
      const searchPart = optimizedSearchParts[i]

      // skip optimization on existing key,value pairs
      if (searchPart.key !== '') continue

      // find the first matching peachPublicId in the searchPart value
      const [match] = peachPublicIdRegExp
        .map(({ key, regExp }) => ({
          key,
          match:
            searchPart.value.match(regExp) ??
            ([] as unknown as RegExpMatchArray),
        }))
        .filter((m) => !!m.match && m.match.index !== undefined)
        .sort((a, b) => (a.match?.index ?? 0) - (b.match?.index ?? 0))

      if (match) {
        // a match exists, split string on match value to build new searchParts
        isOptimized = false
        const { key } = match
        const [value] = match.match
        const matchIndex = match.match.index ?? 0
        const fullValue = optimizedSearchParts[i].value
        const beforeValue = fullValue.substring(0, matchIndex)
        const afterValue = fullValue.substring(matchIndex + value.length)

        if (beforeValue) {
          // if text is before the match, insert a searchPart before match
          optimizedSearchParts.splice(i, 0, {
            key: '',
            value: beforeValue,
            valid: true,
          })
          // increment index; else infinite loop
          i++
        }

        if (afterValue) {
          // if text exists after the match, insert a new searchPart after match
          optimizedSearchParts.push({
            key: '',
            value: afterValue,
            valid: true,
          })
          // do not increment index; we will want to check this new part for matches next
        }

        // replace the searchPart with the optimized version
        optimizedSearchParts[i] = {
          key,
          value,
          valid: true,
        }
      }
    }

    return {
      isOptimized,
      optimizedSearchParts,
    }
  }, [searchParts])

  const usedPrefixes = useMemo(
    () => searchParts.map((p) => p.key).filter((k) => !!k),
    [searchParts],
  )

  const onChangeSearchInput: BorrowerSearchInputChangeHandler = (value) => {
    // newlines might get inserted from clipboard, just remove them
    setSearchInputValue(value.replace('\n', ' '))
  }

  const onSubmitSearchInput: BorrowerSearchInputSubmitHandler = () => {
    const sanitizedSearchInputValue = sanitizeSearchInput(searchParts)
    setSearchInputValue(sanitizedSearchInputValue)
    setSubmittedSearchInputValue(sanitizedSearchInputValue)
  }

  const optimizeSearchInput: BorrowerSearchInputOptimizer = () => {
    const optimizedSearchInputValue = sanitizeSearchInput(optimizedSearchParts)
    setSearchInputValue(optimizedSearchInputValue)
    setSubmittedSearchInputValue(optimizedSearchInputValue)
  }

  const sanitizeSearchInput: BorrowerSearchInputSanitizer = (
    partsToSanitize,
  ) => {
    const keys = partsToSanitize.map((p) => p.key)
    return partsToSanitize
      .filter(
        ({ key, value }, index) =>
          key !== ':' &&
          value !== '' &&
          value !== '"' &&
          value !== '""' &&
          /\S/.test(value) &&
          (key === '' ? true : keys.indexOf(key) === index),
      )
      .map(({ key, value }) => {
        if (key === '') {
          return value.trim()
        }
        if (value.includes(' ')) {
          return `${key}:"${withoutQuotes(value).trim()}"`
        }
        return `${key}:${value.trim()}`
      })
      .join(' ')
  }

  const isColonValidAtIndex: BorrowerSearchInputColonValidator = (index) =>
    index > 0 &&
    index === searchInputValue.length &&
    searchParts[searchParts.length - 1].key === '' &&
    !usedPrefixes.includes(searchParts[searchParts.length - 1].value.trim()) &&
    !!searchInputValue.charAt(index - 1).match(/[a-z]/i)

  useEffect(() => {
    if (searchInputValue === '' && submittedSearchInputValue !== '') {
      setSubmittedSearchInputValue('')
    }
  }, [searchInputValue, submittedSearchInputValue])

  return (
    <BorrowerSearchForm
      isColonValidAtIndex={isColonValidAtIndex}
      isOptimized={isOptimized}
      onChangeSearchInput={onChangeSearchInput}
      onSubmitSearchInput={onSubmitSearchInput}
      optimizeSearchInput={optimizeSearchInput}
      sanitizeSearchInput={sanitizeSearchInput}
      searchInputValue={searchInputValue}
      searchParts={searchParts}
      usedPrefixes={usedPrefixes}
    />
  )
}

export default BorrowerSearch

const withoutQuotes = (str: string) => str.replace(/"/g, '')
